import PropTypes from "prop-types";
import React, { useContext, useEffect } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { ThemeContext } from "../../helpers/ThemeContext";
import NotificationDropdown from "../../components/Common/TopbarDropdown/NotificationDropdown";
import ProfileMenu from "../../components/Common/TopbarDropdown/ProfileMenu";
import { withTranslation } from "react-i18next";
import logoSm from "../../assets/images/logo-sm.png";
import logoLight from "../../assets/images/Kago-color-logo.png";
import {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
} from "../../store/actions";
import Tooltip from "../../utils/toolTip";

const Header = (props) => {
  const { theme, toggleTheme } = useContext(ThemeContext);

  useEffect(() => {
    if (theme === "dark") {
      document.body.classList.add("dark-theme");
      document.body.setAttribute("data-theme", "dark");
    } else {
      document.body.classList.remove("dark-theme");
      document.body.setAttribute("data-theme", "light");
    }
  }, [theme]);

  function toggleFullscreen() {
    if (
      !document.fullscreenElement &&
      !document.mozFullScreenElement &&
      !document.webkitFullscreenElement
    ) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen(
          Element.ALLOW_KEYBOARD_INPUT
        );
      }
    } else {
      if (document.cancelFullScreen) {
        document.cancelFullScreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      }
    }
  }

  return (
    <React.Fragment>
      <style>{`
        #page-topbar {
          background-color: ${theme === "dark" ? "#1a1a1a" : "#ffffff"} !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 1000 !important;
          box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important;
          height: 70px !important;
        }

        .navbar-header {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          height: 70px !important;
          padding: 0 24px !important;
          background-color: transparent !important;
          width: 100% !important;
          max-width: 100% !important;
        }

        /* Logo Container - Desktop shows full logo */
        .navbar-brand-box {
          display: flex !important;
          align-items: center !important;
          justify-content: flex-start !important;
          padding: 0 !important;
          margin: 0 !important;
        }

        .navbar-brand-box .logo {
          display: flex !important;
          align-items: center !important;
          padding: 0 !important;
          margin: 0 !important;
        }

        /* Show only logo-lg on desktop */
        .navbar-brand-box .logo-sm {
          display: none !important;
        }

        .navbar-brand-box .logo-lg {
          display: flex !important;
          align-items: center !important;
        }

        .navbar-brand-box img {
          height: 40px !important;
          width: auto !important;
          max-width: 140px !important;
          object-fit: contain !important;
        }

        /* Theme-based logo visibility */
        body.dark-theme .logo-dark,
        body:not(.dark-theme) .logo-light {
          display: none !important;
        }
        body.dark-theme .logo-light,
        body:not(.dark-theme) .logo-dark {
          display: flex !important;
        }

        /* Right side actions */
        .navbar-header > div:last-child {
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
        }

        .header-item {
          color: ${theme === "dark" ? "#fff" : "#333"} !important;
          background: transparent !important;
          border: none !important;
          padding: 8px !important;
          cursor: pointer !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        .header-item:hover {
          color: ${theme === "dark" ? "#f97316" : "#ea580c"} !important;
        }

        .header-item i {
          font-size: 20px !important;
        }

        /* Mobile - Show only small logo */
        @media (max-width: 768px) {
          #page-topbar {
            height: 75px !important;
          }
          .navbar-header {
            height: 65px !important;
            padding: 0 16px !important;
          }
          .navbar-brand-box .logo-sm {
            display: flex !important;
          }
          .navbar-brand-box .logo-lg {
            display: none !important;
          }
          .navbar-brand-box img {
            height: 32px !important;
          }
        }
      `}
      
      </style>

      <header id="page-topbar">
        <div className="navbar-header">
          {/* Logo Section */}
          <div className="navbar-brand-box">
            <Link to="/dashboard" className="logo logo-dark">
              <span className="logo-sm">
                <img src={logoSm} alt="K" height="32" />
              </span>
              <span className="logo-lg">
                <img src={logoLight} alt="Kago Events" height="40" />
              </span>
            </Link>
            <Link to="/dashboard" className="logo logo-light">
              <span className="logo-sm">
                <img src={logoSm} alt="K" height="32" />
              </span>
              <span className="logo-lg">
                <img src={logoLight} alt="Kago Events" height="40" />
              </span>
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="d-flex align-items-center">
            {/* Theme Toggle */}
            <button
              type="button"
              className="header-item"
              onClick={toggleTheme}
              title={theme === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
            >
              {theme === "light" ? (
                <i className="ri-moon-line"></i>
              ) : (
                <i className="ri-sun-line"></i>
              )}
            </button>

            {/* Fullscreen */}
            <Tooltip text="Full screen">
              <button
                type="button"
                onClick={toggleFullscreen}
                className="header-item d-none d-lg-flex"
                title="Fullscreen"
              >
                <i className="ri-fullscreen-line" />
              </button>
            </Tooltip>

            {/* Notifications */}
            <NotificationDropdown 
              notificationCount={props.notificationCount || 0}
            />

            {/* Profile */}
            <ProfileMenu props={props} />
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

Header.propTypes = {
  changeSidebarType: PropTypes.func,
  leftMenu: PropTypes.any,
  leftSideBarType: PropTypes.any,
  showRightSidebar: PropTypes.any,
  showRightSidebarAction: PropTypes.func,
  t: PropTypes.any,
  toggleLeftmenu: PropTypes.func,
  notificationCount: PropTypes.number,
};

const mapStatetoProps = (state) => {
  const { layoutType, showRightSidebar, leftMenu, leftSideBarType } =
    state.Layout;
  return { layoutType, showRightSidebar, leftMenu, leftSideBarType };
};

export default connect(mapStatetoProps, {
  showRightSidebarAction,
  toggleLeftmenu,
  changeSidebarType,
})(withTranslation()(Header));